// frontend/src/components/auth/LoginScreen.tsx
// Pantalla de inicio de sesión — estilo institucional Bonaerense

import { useState } from 'react';
import { AlertCircle, Lock, LogIn, RefreshCw, User } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { api, type UsuarioSesion } from '../../lib/api';
import { DUR } from '../../lib/motion';

interface LoginScreenProps {
  onLoginSuccess: (token: string, user: UsuarioSesion) => void;
}

export function LoginScreen({ onLoginSuccess }: LoginScreenProps) {
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuario.trim() || !password) {
      setError('Por favor, completa todos los campos.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.login(usuario.trim(), password);
      if (response.success && response.data) {
        const { token, user } = response.data;
        onLoginSuccess(token, user);
      } else {
        setError(response.error || 'Error al iniciar sesión.');
      }
    } catch (err: any) {
      if (err?.message === 'NETWORK_ERROR') {
        setError('No se puede conectar al servidor. Verifica que el backend esté iniciado.');
      } else {
        setError(err?.message || 'Usuario o contraseña incorrectos.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col justify-center items-center p-4 animate-fade-in"
      style={{ backgroundColor: 'var(--color-sgcc-surface)', fontFamily: 'Inter, sans-serif' }}
    >
      <div className="w-full max-w-sm">

        {/* Header — Emblem + Title */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="/brand/sgcc-logo.svg"
            alt=""
            width={168}
            height={150}
            className="w-[168px] h-auto mb-3 select-none"
            draggable={false}
          />
          <h1 className="sr-only">SGCC — Sistema de Gestión Cuerpo de Cadetes</h1>
          <p
            className="text-xs uppercase tracking-widest mt-1"
            style={{ color: 'var(--color-sgcc-outline)' }}
          >
            Presentismo Institucional
          </p>
        </div>

        {/* Login Card */}
        <div
          className="rounded-lg border overflow-hidden"
          style={{
            backgroundColor: 'var(--color-sgcc-surface-container-lowest)',
            borderColor: 'var(--color-sgcc-outline-variant)',
          }}
        >
          {/* Card Header */}
          <div
            className="px-6 py-4 border-b"
            style={{
              backgroundColor: 'var(--color-sgcc-surface-container)',
              borderColor: 'var(--color-sgcc-outline-variant)',
            }}
          >
            <p
              className="font-semibold text-sm uppercase tracking-wider"
              style={{ color: 'var(--color-sgcc-primary)' }}
            >
              Acceso al Sistema
            </p>
          </div>

          {/* Form */}
          <div className="p-6">
            <form id="login-form" onSubmit={handleSubmit} className="space-y-4">

              {/* Error alert */}
              {/* Error: entra con una sacudida (se vuelve a montar en cada intento fallido) */}
              {error && (
                <m.div
                  initial={{ opacity: 0, x: 0 }}
                  animate={{ opacity: 1, x: [0, -8, 8, -5, 5, -2, 0] }}
                  transition={{ opacity: { duration: DUR.rapida }, x: { duration: 0.42, ease: 'easeOut' } }}
                  className="flex items-start gap-2.5 p-3 rounded-lg border text-sm"
                  style={{
                    backgroundColor: 'var(--color-sgcc-error-container)',
                    borderColor: 'rgba(186, 26, 26, 0.3)',
                    color: 'var(--color-sgcc-on-error-container)',
                  }}
                >
                  <AlertCircle size={18} strokeWidth={2} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </m.div>
              )}

              {/* Usuario */}
              <div>
                <label
                  className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
                  style={{ color: 'var(--color-sgcc-outline)' }}
                  htmlFor="login-usuario"
                >
                  Usuario
                </label>
                <div className="relative">
                  <User size={20} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-sgcc-outline)' }} />
                  <input
                    id="login-usuario"
                    type="text"
                    placeholder="Ej: operador"
                    value={usuario}
                    onChange={(e) => setUsuario(e.target.value)}
                    disabled={loading}
                    autoComplete="username"
                    autoFocus
                    className="w-full pl-10 pr-4 h-12 text-sm border rounded-lg outline-none transition-all"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      backgroundColor: 'var(--color-sgcc-surface-container-lowest)',
                      borderColor: 'var(--color-sgcc-outline-variant)',
                      color: 'var(--color-sgcc-on-surface)',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-sgcc-primary)';
                      e.target.style.boxShadow = '0 0 0 2px rgba(0, 30, 64, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-sgcc-outline-variant)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Contraseña */}
              <div>
                <label
                  className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
                  style={{ color: 'var(--color-sgcc-outline)' }}
                  htmlFor="login-password"
                >
                  Contraseña
                </label>
                <div className="relative">
                  <Lock size={20} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-sgcc-outline)' }} />
                  <input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="current-password"
                    className="w-full pl-10 pr-4 h-12 text-sm border rounded-lg outline-none transition-all"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      backgroundColor: 'var(--color-sgcc-surface-container-lowest)',
                      borderColor: 'var(--color-sgcc-outline-variant)',
                      color: 'var(--color-sgcc-on-surface)',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-sgcc-primary)';
                      e.target.style.boxShadow = '0 0 0 2px rgba(0, 30, 64, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-sgcc-outline-variant)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                id="login-submit"
                type="submit"
                disabled={loading || !usuario.trim() || !password.trim()}
                className="w-full h-12 rounded-lg font-semibold text-sm uppercase tracking-wider transition-all tap-active flex items-center justify-center gap-2 mt-2"
                style={{
                  backgroundColor: loading || !usuario.trim() || !password.trim()
                    ? 'var(--color-sgcc-outline-variant)'
                    : 'var(--color-sgcc-primary)',
                  color: 'var(--color-sgcc-on-primary)',
                  cursor: loading || !usuario.trim() || !password.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <m.span
                    key={loading ? 'cargando' : 'listo'}
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6, transition: { duration: DUR.rapida } }}
                  >
                    {loading ? (
                      <>
                        <RefreshCw size={20} strokeWidth={2} className="animate-spin-slow" />
                        <span>Ingresando...</span>
                      </>
                    ) : (
                      <>
                        <LogIn size={20} strokeWidth={2} />
                        <span>Ingresar</span>
                      </>
                    )}
                  </m.span>
                </AnimatePresence>
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p
          className="text-center text-[11px] mt-6"
          style={{ color: 'var(--color-sgcc-outline)' }}
        >
          SGCC v1.0.0 · Policía Bonaerense
        </p>
      </div>
    </div>
  );
}
